import React, {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactElement,
} from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { useToast } from './useToast';
import { VariantProps, cva, cx } from 'class-variance-authority';
import IconButton from '../button/IconButton/IconButton';
import Icon from '../Icon/Icon';
import { cn } from '../../utils';
import styles from './Toast.module.scss';

const Toast = forwardRef<
  ElementRef<typeof ToastPrimitives.Root>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> & {
      description?: string;
      action?: ReactElement<typeof ToastAction>;
    }
>(
  (
    { className, title, description, children, action, variant = 'info', ...props },
    ref
  ) => {
    return (
      <ToastPrimitives.Root
        ref={ref}
        className={cn(toastVariants({ variant }), styles.toastEntranceAnimation)}
        {...props}
      >
        <div className={cn(toastIconWrapperStyles)}>{ICON_PER_VARIANT[variant]}</div>
        <div className='sui-flex-1 sui-py-2 sui-px-1 sui-flex sui-flex-col sui-justify-center'>
          <ToastPrimitives.Title asChild>
            <div className='sui-text-desktop-5 sui-font-bold'>{title}</div>
          </ToastPrimitives.Title>
          {Boolean(description) && (
            <ToastPrimitives.Description asChild>
              <p className='sui-text-neutral-text-weak sui-mt-1 sui-text-desktop-4'>
                {description}
              </p>
            </ToastPrimitives.Description>
          )}
          {children}
        </div>
        {Boolean(action) && action}
        <div className='sui-p-1 sui-flex sui-flex-col sui-justify-center'>
          <ToastPrimitives.Close toast-close='' asChild>
            <IconButton icon='close' sentiment='neutral' />
          </ToastPrimitives.Close>
        </div>
      </ToastPrimitives.Root>
    );
  }
);
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = forwardRef<
  ElementRef<typeof ToastPrimitives.Action>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className='sui-text-action-text sui-text-desktop-4 sui-font-semibold sui-bg-white sui-border-0 sui-cursor-pointer hover:sui-text-action-text-hover active:sui-text-action-text-pressed'
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

type ToastProps = ComponentPropsWithoutRef<typeof Toast>;
type ToastActionElement = ReactElement<typeof ToastAction>;

const ToastProvider = (): JSX.Element => {
  const { toasts } = useToast();

  return (
    <ToastPrimitives.Provider>
      {toasts.map(({ id, title, description, action, ...props }) => (
        <Toast
          key={id}
          title={title}
          description={description}
          action={action}
          {...props}
        />
      ))}
      <ToastPrimitives.Viewport className={cn(toastViewportStyles)} />
    </ToastPrimitives.Provider>
  );
};

export { ToastProvider, type ToastProps, type ToastActionElement, ToastAction, useToast };

const ICON_PER_VARIANT = {
  info: <Icon name='info' />,
  success: <Icon name='check_circle' />,
  warning: <Icon name='warning' />,
  negative: <Icon name='report' />,
};

const toastVariants = cva(
  [
    'sui-group sui-border sui-border-solid sui-flex sui-items-stretch sui-rounded sui-bg-white sui-shadow-up [overflow:hidden]',
    'sui-absolute sui-w-[calc(100%-2rem)] [transition:all_0.2s_ease-in-out]',
  ],
  {
    variants: {
      variant: {
        info: 'sui-border-info-border snap-ui-toast-info',
        success: 'sui-border-positive-border snap-ui-toast-success',
        warning: 'sui-border-warning-border snap-ui-toast-warning',
        negative: 'sui-border-negative-border snap-ui-toast-negative',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

const toastIconWrapperStyles = [
  'sui-px-1 sui-py-2 sui-flex sui-flex-col sui-justify-center',
  'group-[.snap-ui-toast-info]:sui-bg-info-background group-[.snap-ui-toast-info]:sui-text-info-text',
  'group-[.snap-ui-toast-success]:sui-bg-positive-background group-[.snap-ui-toast-success]:sui-text-positive-text',
  'group-[.snap-ui-toast-warning]:sui-bg-warning-background group-[.snap-ui-toast-warning]:sui-text-warning-text',
  'group-[.snap-ui-toast-negative]:sui-bg-negative-background group-[.snap-ui-toast-negative]:sui-text-negative-text',
];

const toastViewportStyles = [
  'sui-fixed sui-z-[1001] sui-flex sui-w-full sui-flex-col sui-p-2 sui-max-w-[478px] [top:0] [right:0]',
  // Note: Hide the first toast when there are more than 3 toasts
  '[&>li]:[top:12px] [&>li]:[scale:0.96]',
  // Note: Hide the second toast when there are more than 2 toasts
  '[&>li:nth-last-child(2)]:[top:20px] [&>li:nth-last-child(2)]:[scale:0.98]',
  // Note: Always show the last toast in the default position and scale
  '[&>li:last-child]:[top:28px] [&>li:last-child]:[scale:1]',
];
